<template>
  <div class="sidebar-layout">
    <!-- Mobile Sidebar Overlay -->
    <div v-if="isMobile && isSidebarOpen" class="sidebar-overlay" @click="isSidebarOpen = false"></div>
    
    <div class="sidebar" :class="{ 'sidebar-mobile': isMobile, 'sidebar-open': isSidebarOpen }">
      <div class="sidebar-header">
        <div class="logo-container">
          <router-link to="/dashboard" class="logo-link">
            <img src="@/assets/logo.png" :alt="t('sidebar.logoAlt')" class="logo-image" />
          </router-link>
        </div>
        <el-button
          v-if="isMobile"
          class="close-button"
          @click="isSidebarOpen = false"
          circle
        >
          <i class="fas fa-times"></i>
        </el-button>
      </div>

      <div class="menu-section">
        <div class="menu-item">
          <router-link :to="pharmacyRoutes.dashboard" class="menu-link">
            <i class="fas fa-chart-line"></i>
            <span>{{ t('sidebar.menu.dashboard') }}</span>
          </router-link>
        </div>

        <div class="menu-group" v-if="accessibleMenuItems.users">
          <div class="menu-header" @click="toggleSubmenu('users')">
            <span class="menu-title">
              <i class="fas fa-users"></i>
              <span>{{ t('sidebar.menu.users') }}</span>
            </span>
            <i class="fas" :class="submenus.users ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </div>
          <transition name="slide-fade">
            <div class="submenu" v-show="submenus.users">
              <router-link v-if="accessibleSubmenuItems.users.list" to="/users" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.usersList') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.users.create" to="/users/create" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.createUser') }}</span>
              </router-link>
            </div>
          </transition>
        </div>

        <!-- Pharmacy Users Management - Only show when pharmacy is assigned -->
        <div class="menu-group" v-if="accessibleMenuItems.pharmacyUsers">
          <div class="menu-header" @click="toggleSubmenu('pharmacyUsers')">
            <span class="menu-title">
              <i class="fas fa-user-md"></i>
              <span>{{ t('sidebar.menu.pharmacyUsers') }}</span>
            </span>
            <i class="fas" :class="submenus.pharmacyUsers ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </div>
          <transition name="slide-fade">
            <div class="submenu" v-show="submenus.pharmacyUsers">
              <router-link v-if="accessibleSubmenuItems.pharmacyUsers.list" :to="pharmacyRoutes.pharmacyUsers.list" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.pharmacyUsersList') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.pharmacyUsers.create" :to="pharmacyRoutes.pharmacyUsers.create" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.createPharmacyUser') }}</span>
              </router-link>
            </div>
          </transition>
        </div>
        
        <div class="menu-group" v-if="accessibleMenuItems.pharmacies">
          <div class="menu-header" @click="toggleSubmenu('pharmacies')">
            <span class="menu-title">
              <i class="fas fa-store"></i>
              <span>{{ t('sidebar.menu.pharmacies') }}</span>
            </span>
            <i class="fas" :class="submenus.pharmacies ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </div>
          <transition name="slide-fade">
            <div class="submenu" v-if="submenus.pharmacies">
              <router-link v-if="accessibleSubmenuItems.pharmacies.list" to="/pharmacies" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.pharmaciesList') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.pharmacies.create" to="/pharmacies/create" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.createPharmacy') }}</span>
              </router-link>
            </div>
          </transition>
        </div>

        <div class="menu-group" v-if="accessibleMenuItems.requests">
          <div class="menu-header" @click="toggleSubmenu('requests')">
            <span class="menu-title">
              <i class="fas fa-clipboard-list"></i>
              <span>{{ t('sidebar.menu.requests') }}</span>
            </span>
            <i class="fas" :class="submenus.requests ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </div>
          <transition name="slide-fade">
            <div class="submenu" v-show="submenus.requests">
              <router-link v-if="accessibleSubmenuItems.requests.list" :to="pharmacyRoutes.requests.list" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.requestsList') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.requests.create" :to="pharmacyRoutes.requests.create" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.createRequest') }}</span>
              </router-link>
            </div>
          </transition>
        </div>

        <div class="menu-group" v-if="accessibleMenuItems.products">
          <div class="menu-header" @click="toggleSubmenu('products')">
            <span class="menu-title">
              <i class="fas fa-box"></i>
              <span>{{ t('sidebar.menu.products') }}</span>
            </span>
            <i class="fas" :class="submenus.products ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </div>
          <transition name="slide-fade">
            <div class="submenu" v-show="submenus.products">
              <router-link v-if="accessibleSubmenuItems.products.list" :to="pharmacyRoutes.products.list" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.productList') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.products.create" :to="pharmacyRoutes.products.create" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.createProduct') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.products.upload" :to="pharmacyRoutes.products.upload" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.csvUpload') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.products.unlinkedReview" :to="pharmacyRoutes.products.unlinkedReview" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.unlinkedProductsReview') }}</span>
              </router-link>
            </div>
          </transition>
        </div>

        <div class="menu-group" v-if="accessibleMenuItems.prescriptions">
          <div class="menu-header" @click="toggleSubmenu('prescriptions')">
            <span class="menu-title">
              <i class="fas fa-file-medical"></i>
              <span>{{ t('sidebar.menu.prescriptions') }}</span>
            </span>
            <i class="fas" :class="submenus.prescriptions ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </div>
          <transition name="slide-fade">
            <div class="submenu" v-show="submenus.prescriptions">
              <router-link v-if="accessibleSubmenuItems.prescriptions.list" :to="pharmacyRoutes.prescriptions.list" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.prescriptionsList') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.prescriptions.create" :to="pharmacyRoutes.prescriptions.create" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.createPrescription') }}</span>
              </router-link>
            </div>
          </transition>
        </div>

        <div class="menu-group" v-if="accessibleMenuItems.developers">
          <div class="menu-header" @click="toggleSubmenu('developers')">
            <span class="menu-title">
              <i class="fas fa-code"></i>
              <span>{{ t('sidebar.menu.developers') }}</span>
            </span>
            <i class="fas" :class="submenus.developers ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </div>
          <transition name="slide-fade">
            <div class="submenu" v-show="submenus.developers">
              <router-link v-if="accessibleSubmenuItems.developers.apiTokens" to="/developers/api-tokens" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.apiTokens') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.developers.apiDocumentation" to="/developers/api-documentation" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.apiDocumentation') }}</span>
              </router-link>
              <router-link v-if="accessibleSubmenuItems.developers.graphqlPlayground" to="/developers/graphql-playground" class="submenu-link" @click.stop>
                <span>{{ t('sidebar.menu.graphqlPlayground') }}</span>
              </router-link>
            </div>
          </transition>
        </div>
      </div>

      <div class="user-section">
        <div class="menu-item">
          <router-link to="/account" class="menu-link">
            <i class="fas fa-user"></i>
            <span>{{ t('sidebar.menu.account') }}</span>
          </router-link>
        </div>
        <div class="menu-item">
          <a href="#" @click.prevent="logout" class="menu-link">
            <i class="fas fa-sign-out-alt"></i>
            <span>{{ t('sidebar.menu.logout') }}</span>
          </a>
        </div>
      </div>
    </div>

    <div class="main-content">
      <div class="top-bar">
        <div class="left-section">
          <el-button
            class="hamburger-button"
            @click="isSidebarOpen = !isSidebarOpen"
            circle
            v-if="isMobile"
          >
            <i class="fas fa-bars"></i>
          </el-button>
          
          <div class="pharmacy-display" v-if="currentUser?.pharmacy">
            <i class="fas fa-store"></i>
            <span>{{ currentUser.pharmacy.name }}</span>
            <el-tag 
              v-if="!currentUser.pharmacy.is_active" 
              type="info" 
              size="small" 
              class="inactive-indicator"
            >
              {{ t('sidebar.pharmacy.inactive') }}
            </el-tag>
          </div>
        </div>
        
        <div class="right-section">
          <!-- Barcode Scanner for Product Search - Only show when user has pharmacy assigned -->
          <BarcodeScanner
            v-if="currentUser?.pharmacy || currentUser?.pharmacy_id"
            :button-text="''"
            button-type=""
            button-size="default"
            :circle="true"
            :plain="false"
            :modal-title="t('barcodeScanner.searchProduct')"
            :instructions="t('barcodeScanner.instructions')"
            @scan-success="handleProductScan"
            class="topbar-scanner header-barcode-scanner"
          >
            <template v-slot:default>
              <i class="fas fa-qrcode"></i>
            </template>
          </BarcodeScanner>
          
          <el-dropdown 
            trigger="click" 
            @visible-change="handleDropdownVisibleChange" 
            placement="bottom-end"
            class="notification-dropdown"
          >
            <el-badge v-if="unreadCount > 0" :value="unreadCount" class="notification-badge">
              <el-button circle>
                <i class="fas fa-bell"></i>
              </el-button>
            </el-badge>
            <el-button v-else circle>
              <i class="fas fa-bell"></i>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <div v-if="loadingNotifications" class="notification-loading">
                  <AppLoader 
                    type="skeleton" 
                    :rows="5" 
                    size="small"
                  />
                </div>
                <div v-else-if="recentNotifications.length === 0" class="notification-empty">
                  <div class="empty-state-content">
                    <i class="far fa-bell-slash empty-icon"></i>
                    <p class="empty-message">{{ t('sidebar.notifications.noNotifications') }}</p>
                  </div>
                </div>
                <template v-else>
                  <el-dropdown-item 
                    v-for="notification in recentNotifications" 
                    :key="notification.id"
                    :class="{ unread: !notification.read_at }"
                  >
                    <div class="notification-icon-bg">
                      <i :class="getNotificationIcon(notification.type)"></i>
                    </div>
                    <div class="notification-preview">
                      <div class="notification-title">{{ getNotificationMessage(notification) }}</div>
                      <div v-if="getNotificationDescription(notification)" class="notification-description">{{ getNotificationDescription(notification) }}</div>
                      <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
                    </div>
                  </el-dropdown-item>
                  <el-dropdown-item divided @click="$router.push('/notifications')" class="more-notifications-btn">
                    <i class="fas fa-bell mr-2"></i>
                    {{ t('notifications.more') }}
                  </el-dropdown-item>
                </template>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <el-dropdown trigger="click" class="account-dropdown">
            <el-button circle>
              <i class="fas fa-user"></i>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu class="account-dropdown-menu">
                <el-dropdown-item v-if="(currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN') && (currentUser?.pharmacy || currentUser?.pharmacy_id)" @click="$router.push(`/pharmacies/edit/${pharmacyId}`)">
                  <i class="fas fa-edit mr-2"></i>
                  {{ t('sidebar.userMenu.editPharmacy') }}
                </el-dropdown-item>
                <el-dropdown-item v-if="currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN'" @click="showPharmacyAssignment = true">
                  <i class="fas fa-store mr-2"></i>
                  {{ t('sidebar.userMenu.assignPharmacy') }}
                </el-dropdown-item>
                <el-dropdown-item v-if="(currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN') && (currentUser?.pharmacy || currentUser?.pharmacy_id)" @click="handleClearPharmacy">
                  <i class="fas fa-times mr-2"></i>
                  {{ t('sidebar.userMenu.clearPharmacy') }}
                </el-dropdown-item>

                <el-dropdown-item @click="$router.push('/account')">
                  <i class="fas fa-user mr-2"></i>
                  {{ t('sidebar.userMenu.accountSettings') }}
                </el-dropdown-item>
                <el-dropdown-item divided @click="logout">
                  <i class="fas fa-sign-out-alt mr-2"></i>
                  {{ t('sidebar.userMenu.logout') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
      <div class="content-wrapper">
        <!-- Email Verification Banner -->
        <div v-if="currentUser && !currentUser.email_verified_at" class="email-verification-banner">
          <i class="fas fa-exclamation-triangle"></i>
          <span>{{ t('messages.email_not_verified') }}</span>
          <el-button size="small" @click="resendVerificationEmail" :loading="resendingVerification">
            {{ t('actions.resend_verification') }}
          </el-button>
        </div>
        <router-view></router-view>
      </div>
    </div>
    
    <!-- Pharmacy Assignment Modal -->
    <PharmacyAssignmentModal 
      v-model="showPharmacyAssignment"
      @assigned="handlePharmacyAssigned"
    />
    

  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useI18n } from 'vue-i18n';
import { ElMessageBox, ElNotification } from 'element-plus';

import { useQuery, useMutation } from '@vue/apollo-composable';
import { logoutMixin } from "@/mixins/logout";
import { getAccessibleMenuItems, getAccessibleSubmenuItems, isAdmin, isSuperAdmin, hasPharmacyAssigned, canManagePharmacyUsers } from "@/utils/roleAccess";
import PharmacyAssignmentModal from "@/components/PharmacyAssignmentModal.vue";
import AppLoader from "@/components/AppLoader.vue";
import BarcodeScanner from "@/components/BarcodeScanner.vue";
import * as NotificationsGraphQL from "@/graphql/notifications";
import { GET_PHARMACY_PRODUCT_BY_GTIN } from "@/graphql/pharmacy-products";
import { RESEND_EMAIL_VERIFICATION_MUTATION } from "@/graphql/auth-user";


export default {
  name: "SidebarLayout",
  components: {
    PharmacyAssignmentModal,
    AppLoader,
    BarcodeScanner
  },
  mixins: [logoutMixin],
  setup() {
    const store = useStore();
    const router = useRouter();
    const { t } = useI18n();
    const isSidebarOpen = ref(false);
    const isMobile = ref(false);
    const submenus = ref({
      requests: false,
      developers: false,
      pharmacies: false,
      prescriptions: false,
      products: false,
      users: false,
      pharmacyUsers: false,
    });

    const currentUser = computed(() => store.getters["user/user"]);
    const pharmacyId = computed(() => currentUser.value?.pharmacy_id || currentUser.value?.pharmacy?.id);
    const currentPharmacy = computed(() => currentUser.value?.pharmacy);
    
    // Route helpers for pharmacy-specific routes
    const pharmacyRoutes = computed(() => {
      try {
        const hasPharmacy = hasPharmacyAssigned(currentUser.value)
        const validPharmacyId = pharmacyId.value && pharmacyId.value !== 'undefined' && pharmacyId.value !== 'null'
        
        const routes = {
          dashboard: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/dashboard` : '/dashboard',
          products: {
            list: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/products` : '/products',
            create: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/products/create` : '/products/create',
            upload: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/products/upload` : '/products/upload',
            unlinkedReview: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/products/unlinked-review` : '/products/unlinked-review',
            detail: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/products/${id}` : `/products/${id}`,
            edit: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/products/${id}/edit` : `/products/edit/${id}`
          },
          requests: {
            list: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/requests` : '/requests',
            create: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/requests/search` : '/requests/search',
            detail: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/requests/${id}` : `/requests/show/${id}`,
            offer: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/requests/${id}/offer` : `/requests/offer/${id}`,
            search: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/requests/search` : '/requests/search'
          },
          prescriptions: {
            list: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/prescriptions` : '/prescriptions',
            create: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/prescriptions/create` : '/prescriptions/create',
            detail: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/prescriptions/${id}` : `/prescriptions/show/${id}`
          },
          users: {
            list: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/users` : '/users',
            create: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/users/create` : '/users/create',
            detail: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/users/${id}` : `/users/show/${id}`,
            edit: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/users/${id}/edit` : `/users/edit/${id}`
          },
          pharmacyUsers: {
            list: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/users` : '/users',
            create: hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/users/create` : '/users/create',
            detail: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/users/${id}` : `/users/show/${id}`,
            edit: (id) => hasPharmacy && validPharmacyId ? `/pharmacies/${pharmacyId.value}/users/${id}/edit` : `/users/edit/${id}`
          }
        }

        console.log('Pharmacy routes generated:', {
          hasPharmacy,
          pharmacyId: pharmacyId.value,
          validPharmacyId,
          routes
        })

        return routes
      } catch (error) {
        console.error('Error generating pharmacy routes:', error)
        return {
          dashboard: '/dashboard',
          products: { list: '/products', create: '/products/create', upload: '/products/upload' },
          requests: { list: '/requests', create: '/requests/search' },
          prescriptions: { list: '/prescriptions', create: '/prescriptions/create' },
          users: { list: '/users', create: '/users/create' },
          pharmacyUsers: { list: '/users', create: '/users/create' }
        }
      }
    });
    
    // Role-based access control
    const accessibleMenuItems = computed(() => {
      try {
        return getAccessibleMenuItems(currentUser.value) || {}
      } catch (error) {
        console.error('Error getting accessible menu items:', error)
        return {}
      }
    });
    const accessibleSubmenuItems = computed(() => {
      try {
        return getAccessibleSubmenuItems(currentUser.value) || {}
      } catch (error) {
        console.error('Error getting accessible submenu items:', error)
        return {}
      }
    });
    
    // Pharmacy assignment modal
    const showPharmacyAssignment = ref(false);
    
    // Email verification resend
    const resendingVerification = ref(false);

    // Notification functionality
    const loadingNotifications = computed(() => {
      try {
        return recentNotificationsLoading.value || false;
      } catch (error) {
        console.error('Error computing loading state:', error);
        return false;
      }
    });

    // GraphQL query for notification stats (standard pattern like other components)
    const { result: notificationStatsResult } = useQuery(
      NotificationsGraphQL.GET_NOTIFICATION_STATS,
      null, // No variables needed
      { 
        fetchPolicy: 'cache-and-network',
        pollInterval: 30000, // Poll every 30 seconds
        errorPolicy: 'all'
      }
    );

    // GraphQL query for recent unread notifications (NotificationService handles polling)
    const { result: recentNotificationsResult, loading: recentNotificationsLoading, refetch: recentNotificationsRefetch } = useQuery(
      NotificationsGraphQL.GET_UNREAD_NOTIFICATIONS,
      null, // No variables needed
      { 
        fetchPolicy: 'cache-only', // Use cache only - NotificationService invalidates when needed
        errorPolicy: 'all'
        // Removed pollInterval - NotificationService handles polling
      }
    );

    // Notifications are now handled by NotificationService with proper polling and caching
    // No need for fallback polling here
    const startFallbackPolling = () => {
      // Disabled: NotificationService handles notification polling
      console.log('✅ Fallback polling disabled - using NotificationService');
    };
    
    const stopFallbackPolling = () => {
      // Disabled: NotificationService handles notification polling
      console.log('🛑 Fallback polling disabled');
    };

    // Periodic user data refresh to detect pharmacy assignment changes
    let userDataRefreshInterval = null;
    
    const startUserDataRefresh = () => {
      if (userDataRefreshInterval) {
        clearInterval(userDataRefreshInterval);
      }
      
      // Check if user is admin/super admin (with or without pharmacy assignment)
      // We need to poll for both cases:
      // - Admin with pharmacy: to detect when pharmacy is removed/changed
      // - Admin without pharmacy: to detect when pharmacy is assigned
      const user = currentUser.value;
      const isAdmin = user && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN');
      
      if (!isAdmin) {
        console.log('⏭️ Skipping user data refresh - user is not admin/super admin');
        return;
      }
      
      console.log('🔄 Starting periodic user data refresh to detect pharmacy assignment changes...');
      
      userDataRefreshInterval = setInterval(async () => {
        try {
          console.log('🔄 Periodic user data refresh: checking for pharmacy assignment changes...');
          // This will trigger the check in fetchUser action
          await store.dispatch('user/fetchUser');
          console.log('✅ User data refresh completed');
        } catch (error) {
          console.error('❌ User data refresh error:', error);
        }
      }, 30000); // Every 30 seconds
      
      console.log('✅ User data refresh polling started');
    };
    
    const stopUserDataRefresh = () => {
      if (userDataRefreshInterval) {
        clearInterval(userDataRefreshInterval);
        userDataRefreshInterval = null;
        console.log('🛑 User data refresh polling stopped');
      }
    };

    // Set up both mutations
    const { mutate: markAsReadMutation } = useMutation(
      NotificationsGraphQL.MARK_NOTIFICATION_AS_READ,
      {
        errorPolicy: 'all'
      }
    );



    // Debug the mutation setup
    console.log('🔧 Mutation setup:', {
      mutationAvailable: !!markAsReadMutation,
      mutationQuery: NotificationsGraphQL.MARK_NOTIFICATION_AS_READ
    });

    const notificationStats = computed(() => {
      try {
        if (!notificationStatsResult.value) {
          return { total: 0, unread: 0, read: 0 }
        }
        return notificationStatsResult.value.notificationStats || { total: 0, unread: 0, read: 0 }
      } catch (error) {
        console.error('Error computing notification stats:', error)
        return { total: 0, unread: 0, read: 0 }
      }
    });
    
    const recentNotifications = computed(() => {
      try {
        if (!recentNotificationsResult.value) {
          return []
        }
        
        const notifications = recentNotificationsResult.value.notifications;
        
        if (!notifications) {
          return []
        }
        
        const notificationsArray = Array.isArray(notifications) ? notifications : [];
        
        // Get latest 5 notifications
        return notificationsArray.slice(0, 5);
      } catch (error) {
        console.error('Error computing recent notifications:', error)
        return []
      }
    });
    
    // Use store for real-time notification count with manual reactivity
    const localUnreadCount = ref(store.getters['notifications/unreadCount'] || 0);
    
    const unreadCount = computed(() => {
      try {
        const count = localUnreadCount.value;
        console.log('🔢 unreadCount computed property called, returning:', count);
        return count;
      } catch (error) {
        console.error('Error getting unread count from store:', error)
        return 0
      }
    });
    
    // Watch for store changes and update local ref
    watch(() => store.getters['notifications/unreadCount'], (newCount) => {
      console.log('👀 Store unreadCount changed, updating local ref:', newCount);
      localUnreadCount.value = newCount || 0;
    }, { immediate: true });





    // Timer for auto-marking notifications as read
    let markAsReadTimer = null;

    // Handle dropdown visibility change
    const handleDropdownVisibleChange = async (visible) => {
      console.log('🔄 Dropdown visibility changed:', visible);
      if (visible) {
        // Refetch notifications when dropdown opens to ensure fresh data
        if (recentNotificationsRefetch) {
          console.log('🔄 Refetching notifications...');
          recentNotificationsRefetch();
        }
        
        // Mark visible unread notifications as read immediately for better UX
        console.log('🚀 Marking visible notifications as read immediately');
        markVisibleNotificationsAsRead();
      } else {
        console.log('❌ Dropdown closed');
        // Clear any existing timer
        if (markAsReadTimer) {
          clearTimeout(markAsReadTimer);
          markAsReadTimer = null;
        }
      }
    };

    // Mark visible unread notifications as read (bulk approach)
    const markVisibleNotificationsAsRead = async () => {
      try {
        console.log('🔔 markVisibleNotificationsAsRead triggered');
        const visibleNotifications = recentNotifications.value || [];
        const unreadNotifications = visibleNotifications.filter(n => !n.read_at);
        
        if (unreadNotifications.length === 0) {
          console.log('✅ No unread notifications to mark');
          return;
        }

        console.log(`🚀 Bulk marking ${unreadNotifications.length} notifications as read`);

        // Approach: Use Promise.all for parallel execution instead of sequential
        const markPromises = unreadNotifications.map(async (notification) => {
          try {
            console.log(`📝 Marking notification ${notification.id} as read`);
            
            await markAsReadMutation(
              { id: notification.id },
              {
                // Don't refetch for each individual mutation - we'll do it once at the end
                refetchQueries: []
              }
            );
            
            // Local state update removed - GraphQL objects are read-only
            
            return { success: true, id: notification.id };
          } catch (error) {
            console.error(`❌ Failed to mark notification ${notification.id} as read:`, error);
            return { success: false, id: notification.id, error };
          }
        });

        // Execute all mutations in parallel
        const results = await Promise.all(markPromises);
        
        // Count successful mutations
        const successfulMarks = results.filter(r => r.success).length;
        
        if (successfulMarks > 0) {
          // Immediately update local store count for responsive UI
          console.log(`📊 Updating local store count by -${successfulMarks}`);
          for (let i = 0; i < successfulMarks; i++) {
            store.dispatch('notifications/decrementUnreadCount');
          }
          
          // Then refresh from database to ensure consistency
          try {
            console.log('🔄 Refreshing notification counter from database...');
            console.log('📊 Store count before refresh:', store.getters['notifications/unreadCount']);
            await store.dispatch('notifications/fetchNotificationStats');
            console.log('✅ Notification counter refreshed from database');
            console.log('📊 Store count after refresh:', store.getters['notifications/unreadCount']);
          } catch (error) {
            console.error('❌ Error refreshing notification stats:', error);
            // Local store update already done above, so UI remains responsive
          }
          
          // Refetch notifications list
          try {
            if (recentNotificationsRefetch) {
              await recentNotificationsRefetch();
            }
          } catch (error) {
            console.error('Error refetching notifications:', error);
          }
        }
        
        console.log(`🎉 Bulk operation complete: ${successfulMarks}/${unreadNotifications.length} marked successfully`);
      } catch (error) {
        console.error('❌ Error in bulk marking notifications as read:', error);
      }
    };

    const getNotificationIcon = () => {
      // Always return green bell icon for all notifications
      return 'fas fa-bell';
    };

    const getNotificationTitle = (notification) => {
      try {
        if (!notification || !notification.data) {
          return t('notifications.titles.default');
        }
        
        let data = notification.data;
        
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            data = {};
          }
        }
        
        if (!data || typeof data !== 'object') {
          data = {};
        }
        
        // Handle the new notification data structure from backend
        if (data.title) {
          return data.title;
        }
        
        // Legacy handling for specific notification types
        switch (notification.type) {
          case 'stock_request_created':
            return t('notifications.titles.stockRequestCreated', { 
              product: data.product_name || 'Unknown Product',
              pharmacy: data.pharmacy_name || 'Unknown Pharmacy'
            });
          
          case 'stock_request_status_changed':
            return t('notifications.titles.stockRequestStatusChanged', { 
              product: data.product_name || 'Unknown Product',
              oldStatus: data.old_status || 'Unknown',
              newStatus: data.new_status || 'Unknown'
            });
          
          case 'offer_received':
            return t('notifications.titles.offerReceived', { 
              product: data.product_name || 'Unknown Product'
            });
          
          case 'offer_accepted':
            return t('notifications.titles.offerAccepted', { 
              product: data.product_name || 'Unknown Product'
            });
          
          case 'system_notification':
            return data.title || t('notifications.titles.systemNotification');
          
          default:
            return t('notifications.titles.default');
        }
      } catch (error) {
        console.error('Error getting notification title:', error);
        return t('notifications.titles.default');
      }
    };

    const getNotificationMessage = (notification) => {
      let data = notification.data;
      
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          data = {};
        }
      }
      
      if (!data || typeof data !== 'object') {
        data = {};
      }
      
      
      // Handle the new notification data structure from backend
      if (data.message) {
        return data.message;
      }
      
      // Check nested data structure (data.data.message)
      if (data.data && data.data.message) {
        return data.data.message;
      }
      
      // Legacy handling for specific notification types (prioritize detailed messages)
      switch (notification.type) {
        case 'stock_request_created':
          return `New stock request for ${data.product_name || 'a product'} from ${data.pharmacy_name || 'a pharmacy'}`;
        case 'stock_request_status_changed':
          return `Status changed from ${data.old_status || 'Unknown'} to ${data.new_status || 'Unknown'}`;
        case 'offer_received':
          return `You received an offer for ${data.product_name || 'a product'}`;
        case 'offer_accepted':
          return `Your offer for ${data.product_name || 'a product'} was accepted`;
        case 'system_notification':
          return data.message || 'System notification';
        default:
          // Fallback to title if no specific handling
          return data.title || 'Notification message';
      }
    };

    const getNotificationDescription = (notification) => {
      let data = notification.data;
      
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          data = {};
        }
      }
      
      if (!data || typeof data !== 'object') {
        data = {};
      }
      
      // Return the description field from the backend notification data
      if (data.description) {
        return data.description;
      }
      
      // Fallback: create description based on notification type
      switch (notification.type) {
        case 'stock_request_created':
          return `Quantity: ${data.quantity_needed || 'N/A'} | Urgency: ${data.urgency_level || 'N/A'} | Distance: ${data.max_distance || 'N/A'} km`;
        case 'stock_request_status_changed':
          return `Product: ${data.product_name || 'Unknown'} | Request ID: ${data.request_id || 'N/A'}`;
        case 'offer_received':
          return `From: ${data.pharmacy_name || 'Unknown Pharmacy'} | Quantity: ${data.quantity || 'N/A'} | Price: $${data.price || 'N/A'}`;
        case 'offer_accepted':
          return `Request: ${data.product_name || 'Unknown Product'} | Quantity: ${data.quantity || 'N/A'} | Price: $${data.price || 'N/A'}`;
        default:
          return null;
      }
    };

    const formatTime = (timestamp) => {
      try {
        if (!timestamp) return '';
        
        let date;
        
        // Handle timezone issue: if timestamp is a string without timezone info,
        // treat it as UTC time (since backend likely stores in UTC)
        if (typeof timestamp === 'string' && !timestamp.includes('T') && !timestamp.includes('Z')) {
          // Convert "2025-08-03 01:01:20" to UTC format "2025-08-03T01:01:20Z"
          const utcTimestamp = timestamp.replace(' ', 'T') + 'Z';
          date = new Date(utcTimestamp);
        } else {
          date = new Date(timestamp);
        }
        
        if (isNaN(date.getTime())) return '';
        
        const now = new Date();
        const diffInMs = now - date;
        
        // If still negative after timezone fix, it might be a legitimate future timestamp
        // In that case, show "just now" as fallback
        if (diffInMs < 0) {
          return t('notifications.time.justNow');
        }
        
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        
        if (diffInMinutes < 1) {
          return t('notifications.time.justNow');
        } else if (diffInMinutes === 1) {
          return t('notifications.time.minuteAgo');
        } else if (diffInMinutes < 60) {
          return t('notifications.time.minutesAgo', { minutes: diffInMinutes });
        } else if (diffInHours === 1) {
          return t('notifications.time.hourAgo');
        } else if (diffInHours < 24) {
          return t('notifications.time.hoursAgo', { hours: diffInHours });
        } else if (diffInDays === 1) {
          return t('notifications.time.dayAgo');
        } else if (diffInDays < 7) {
          return t('notifications.time.daysAgo', { days: diffInDays });
        } else {
          return date.toLocaleDateString();
        }
      } catch (error) {
        console.error('Error formatting time:', error);
        return '';
      }
    };

    const handleNotificationClick = async (notification) => {
      // Mark as read if unread
      if (!notification.read_at && markAsReadMutation) {
        try {
          // Use correct mutation syntax
          await markAsReadMutation(
            { id: notification.id },
            {
              refetchQueries: [
                { query: NotificationsGraphQL.GET_NOTIFICATION_STATS },
                { query: NotificationsGraphQL.GET_UNREAD_NOTIFICATIONS }
              ]
            }
          );
          
          // Refresh notification stats from database for accuracy
          try {
            await store.dispatch('notifications/fetchNotificationStats');
            console.log('✅ Individual notification marked as read, counter refreshed');
          } catch (error) {
            console.error('❌ Error refreshing stats after individual mark:', error);
            // Fallback: manual decrement
            store.dispatch('notifications/decrementUnreadCount');
          }
        } catch (error) {
          console.error('Failed to mark notification as read:', error);
        }
      }
      
      // Handle navigation if URL is provided
      if (notification.data && notification.data.url) {
        window.open(notification.data.url, '_blank');
      }
    };

    const toggleSubmenu = (menu) => {
      // Close all other submenus first
      Object.keys(submenus.value).forEach(key => {
        if (key !== menu) {
          submenus.value[key] = false;
        }
      });
      
      // Toggle the clicked menu
      submenus.value[menu] = !submenus.value[menu];
    };

    onMounted(() => {
      try {
        // Check if the device is mobile
        isMobile.value = window.innerWidth <= 768;
        // Set sidebar to closed if mobile
        if (isMobile.value) {
          isSidebarOpen.value = false;
        }
        window.addEventListener("resize", handleResize);
        
        // Auto-expand submenu based on current route
        const currentRoute = router.currentRoute.value.path;
        console.log('onMounted - Initial route:', currentRoute);
        
        // Use same logic as watch() for consistency
        if (currentRoute.includes('/users')) {
          // Check if this is a pharmacy-specific user route
          const hasPharmacy = hasPharmacyAssigned(currentUser.value);
          if (hasPharmacy && currentRoute.includes('/pharmacies/')) {
            console.log('onMounted - Opening pharmacyUsers submenu for:', currentRoute);
            submenus.value.pharmacyUsers = true;
          } else {
            console.log('onMounted - Opening users submenu for:', currentRoute);
            submenus.value.users = true;
          }
        } else if (currentRoute.includes('/requests')) {
          console.log('onMounted - Opening requests submenu for:', currentRoute);
          submenus.value.requests = true;
        } else if (currentRoute.includes('/products')) {
          console.log('onMounted - Opening products submenu for:', currentRoute);
          submenus.value.products = true;
        } else if (currentRoute.includes('/prescriptions')) {
          console.log('onMounted - Opening prescriptions submenu for:', currentRoute);
          submenus.value.prescriptions = true;
        } else if (currentRoute.startsWith('/pharmacies') && !currentRoute.includes('/products') && !currentRoute.includes('/requests') && !currentRoute.includes('/prescriptions') && !currentRoute.includes('/users')) {
          console.log('onMounted - Opening pharmacies submenu for:', currentRoute);
          submenus.value.pharmacies = true;
        } else if (currentRoute.startsWith('/developers')) {
          console.log('onMounted - Opening developers submenu for:', currentRoute);
          submenus.value.developers = true;
        }
        
        console.log('onMounted - Submenus state:', submenus.value);
        
        // Fetch user data if not already loaded
        if (!currentUser.value?.id) {
          store.dispatch('user/fetchUser');
        }
        
        // Simple test to see if user data is loaded
        setTimeout(() => {
          console.log('Sidebar test - User:', currentUser.value);
          console.log('Sidebar test - User pharmacy:', currentUser.value?.pharmacy);
          console.log('Sidebar test - User pharmacy_id:', currentUser.value?.pharmacy_id);
          console.log('Sidebar test - Computed pharmacyId:', pharmacyId.value);
          console.log('Sidebar test - User role:', currentUser.value?.role);
          console.log('Sidebar test - Has pharmacy:', hasPharmacyAssigned(currentUser.value));
          console.log('Sidebar test - Pharmacy routes:', pharmacyRoutes.value);
          console.log('Sidebar test - Accessible menu items:', accessibleMenuItems.value);
          console.log('Sidebar test - Accessible submenu items:', accessibleSubmenuItems.value);
          console.log('Sidebar test - Pharmacy users menu visible:', accessibleMenuItems.value?.pharmacyUsers);
          console.log('Sidebar test - Can manage pharmacy users:', canManagePharmacyUsers(currentUser.value));
          console.log('Sidebar test - Account dropdown conditions:', {
            userRole: currentUser.value?.role,
            isAdmin: isAdmin(currentUser.value),
            isSuperAdmin: isSuperAdmin(currentUser.value),
            hasPharmacy: hasPharmacyAssigned(currentUser.value),
            canEditPharmacy: currentUser.value?.role === 'ADMIN' || currentUser.value?.role === 'SUPER_ADMIN',
            canAssignPharmacy: currentUser.value?.role === 'ADMIN' || currentUser.value?.role === 'SUPER_ADMIN',
            canClearPharmacy: currentUser.value?.role === 'ADMIN' || currentUser.value?.role === 'SUPER_ADMIN'
          });
        }, 1000);
        
        // Start fallback polling to ensure notifications stay fresh
        startFallbackPolling();
        
        // Start periodic user data refresh to detect pharmacy assignment changes
        // Wait a bit for user data to be loaded first
        setTimeout(() => {
          startUserDataRefresh();
        }, 2000);
      } catch (error) {
        console.error('Error in onMounted:', error);
      }
    });

    // Watch for route changes to auto-expand correct submenu
    watch(() => router.currentRoute.value.path, (newPath) => {
      try {
        console.log('Route changed to:', newPath);
        
        // Reset all submenus first
        Object.keys(submenus.value).forEach(key => {
          submenus.value[key] = false;
        });
        
        // Determine which submenu to open based on route path
        if (newPath.includes('/users')) {
          // Check if this is a pharmacy-specific user route
          const hasPharmacy = hasPharmacyAssigned(currentUser.value);
          if (hasPharmacy && newPath.includes('/pharmacies/')) {
            console.log('Opening pharmacyUsers submenu for:', newPath);
            submenus.value.pharmacyUsers = true;
          } else {
            console.log('Opening users submenu for:', newPath);
            submenus.value.users = true;
          }
        } else if (newPath.includes('/requests')) {
          console.log('Opening requests submenu for:', newPath);
          submenus.value.requests = true;
        } else if (newPath.includes('/products')) {
          console.log('Opening products submenu for:', newPath);
          submenus.value.products = true;
        } else if (newPath.includes('/prescriptions')) {
          console.log('Opening prescriptions submenu for:', newPath);
          submenus.value.prescriptions = true;
        } else if (newPath.startsWith('/pharmacies') && !newPath.includes('/products') && !newPath.includes('/requests') && !newPath.includes('/prescriptions') && !newPath.includes('/users')) {
          console.log('Opening pharmacies submenu for:', newPath);
          submenus.value.pharmacies = true;
        } else if (newPath.startsWith('/developers')) {
          console.log('Opening developers submenu for:', newPath);
          submenus.value.developers = true;
        }
        
        console.log('Submenus state:', submenus.value);
      } catch (error) {
        console.error('Error in route watch:', error);
      }
    });

    // Watch for user role or pharmacy assignment changes to restart polling
    watch(() => {
      const user = currentUser.value;
      if (!user) return null;
      return {
        role: user.role,
        pharmacyId: user.pharmacy_id || user.pharmacy?.id || null
      };
    }, (newVal, oldVal) => {
      try {
        // Restart polling if user role or pharmacy assignment changed
        if (newVal && oldVal) {
          const wasAdmin = oldVal.role && 
            (oldVal.role === 'ADMIN' || oldVal.role === 'SUPER_ADMIN');
          
          const isAdmin = newVal.role && 
            (newVal.role === 'ADMIN' || newVal.role === 'SUPER_ADMIN');
          
          // Restart polling if admin status changed or pharmacy assignment changed
          if (wasAdmin !== isAdmin || (wasAdmin && isAdmin && oldVal.pharmacyId !== newVal.pharmacyId)) {
            console.log('🔄 User status changed, restarting user data refresh polling...');
            stopUserDataRefresh();
            if (isAdmin) {
              setTimeout(() => {
                startUserDataRefresh();
              }, 500);
            }
          }
        }
      } catch (error) {
        console.error('Error in user pharmacy assignment watch:', error);
      }
    }, { deep: true });

    onUnmounted(() => {
      try {
        window.removeEventListener("resize", handleResize);
        
        // Clean up notification mark-as-read timer
        if (markAsReadTimer) {
          clearTimeout(markAsReadTimer);
          markAsReadTimer = null;
        }
        
        // Stop fallback polling
        stopFallbackPolling();
        
        // Stop user data refresh polling
        stopUserDataRefresh();
      } catch (error) {
        console.error('Error in onUnmounted:', error);
      }
    });

    const handleResize = () => {
      try {
        isMobile.value = window.innerWidth <= 768;
        // Update sidebar state based on screen size
        if (isMobile.value) {
          isSidebarOpen.value = false;
        }
      } catch (error) {
        console.error('Error handling resize:', error);
      }
    };

    const handlePharmacyAssigned = () => {
      // Pharmacy was successfully assigned - hard redirect is now handled in the modal
      // This method is kept for backward compatibility but no longer needed
      console.log('Pharmacy assignment completed - hard redirect handled in modal');
    };

    const handleClearPharmacy = () => {
      ElMessageBox.confirm(t('sidebar.userMenu.clearPharmacyConfirm'), t('sidebar.userMenu.clearPharmacyTitle'), {
        confirmButtonText: t('sidebar.userMenu.clearPharmacyConfirmButton'),
        cancelButtonText: t('sidebar.userMenu.clearPharmacyCancelButton'),
        type: 'warning',
      }).then(() => {
        store.dispatch('user/clearPharmacy').then(() => {
          store.dispatch('user/fetchUser').then(() => {
            ElNotification({
              title: t('sidebar.userMenu.pharmacyClearedTitle'),
              message: t('sidebar.userMenu.pharmacyCleared'),
              type: 'success',
              duration: 3000,
              position: 'bottom-right',
              icon: 'fas fa-bell',
              customClass: 'notification-success-message'
            });
            // Redirect to admin dashboard after clearing pharmacy impersonation
            // Add a small delay to ensure state is updated
            setTimeout(() => {
              router.push('/dashboard');
            }, 100);
          });
        }).catch(error => {
          console.error('Error clearing pharmacy:', error);
          ElNotification({
            title: t('sidebar.userMenu.pharmacyClearErrorTitle'),
            message: t('sidebar.userMenu.pharmacyClearError'),
            type: 'error',
            duration: 3000,
            position: 'bottom-right'
          });
        });
      }).catch(() => {
        // User cancelled
      });
    };

    // Handle barcode scan for product search
    const handleProductScan = async (scannedCode) => {
      try {
        const { data } = await window.apolloClient.query({
          query: GET_PHARMACY_PRODUCT_BY_GTIN,
          variables: {
            gtin: scannedCode,
            pharmacy_id: pharmacyId.value
          }
        });

        if (data.pharmacyProductByGtin) {
          // Product found - navigate to product detail page
          const productId = data.pharmacyProductByGtin.id;
          router.push(pharmacyRoutes.value.products.detail(productId));
          
          ElNotification({
            title: t('barcodeScanner.scanSuccess'),
            message: `${t('common.product')}: ${data.pharmacyProductByGtin.display_name}`,
            type: 'success',
            position: 'bottom-right'
          });
        } else {
          // Product not found
          ElNotification({
            title: t('barcodeScanner.noProduct'),
            message: `${t('barcodeScanner.scannedCode')}: ${scannedCode}`,
            type: 'warning',
            position: 'bottom-right'
          });
        }
      } catch (error) {
        console.error('Error looking up product by GTIN:', error);
        ElNotification({
          title: t('common.error'),
          message: t('barcodeScanner.scannerError'),
          type: 'error',
          position: 'bottom-right'
        });
      }
    };

    // Resend email verification
    const { mutate: resendEmailVerificationMutation } = useMutation(RESEND_EMAIL_VERIFICATION_MUTATION);
    
    const resendVerificationEmail = async () => {
      if (!currentUser.value?.email) return;
      
      resendingVerification.value = true;
      try {
        const { data } = await resendEmailVerificationMutation({
          input: {
            email: currentUser.value.email
          }
        });
        
        if (data?.resendEmailVerification?.success) {
          ElNotification({
            title: data.resendEmailVerification.message,
            type: 'success',
            position: 'bottom-right'
          });
        } else {
          ElNotification({
            title: data?.resendEmailVerification?.message || 'Failed to send verification email',
            type: 'error',
            position: 'bottom-right'
          });
        }
      } catch (error) {
        console.error('Error resending verification:', error);
        ElNotification({
          title: 'Error sending verification email',
          type: 'error',
          position: 'bottom-right'
        });
      } finally {
        resendingVerification.value = false;
      }
    };

    return {
      t,
      isSidebarOpen,
      isMobile,
      submenus,
      toggleSubmenu,
      pharmacyId,
      accessibleMenuItems,
      accessibleSubmenuItems,
      showPharmacyAssignment,
      handlePharmacyAssigned,
      isAdmin,
      isSuperAdmin,
      hasPharmacyAssigned,
      canManagePharmacyUsers,
      currentUser,
      currentPharmacy,
      handleClearPharmacy,
      pharmacyRoutes,
      // Email verification
      resendingVerification,
      resendVerificationEmail,
      // Notification functionality
      notificationStats,
      recentNotifications,
      loadingNotifications,
      unreadCount,
      getNotificationIcon,
      getNotificationTitle,
      getNotificationMessage,
      getNotificationDescription,
      formatTime,
      handleNotificationClick,
      handleDropdownVisibleChange,
      handleProductScan
    };
  },
};
</script>

<style>
.account-dropdown-menu li.el-dropdown-menu__item{
  padding: 10px 16px !important;
}
</style>

<style scoped>
.sidebar-layout {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  width: 300px;
  background: linear-gradient(180deg, #000000 0%, #0a0a0a 75%, rgba(64,109,112,143) 100%) !important;
  padding: 20px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #333333;
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  z-index: 100;
}

.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.sidebar-mobile {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 1000;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  display: none;
}

.sidebar-open {
  transform: translateX(0);
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-left: 10px;
}

.logo-container {
  margin-bottom: 30px;
  text-align: left;
  padding-left: 10px;
}

.logo-image {
  max-width: 200px;
  height: auto;
}

.logo-link {
  text-decoration: none;
  color: inherit;
  display: block;
  transition: opacity 0.2s ease;
}

.logo-link:hover {
  opacity: 0.8;
  text-decoration: none;
  color: inherit;
}

.menu-section {
  flex: 1;
}

.user-section {
  margin-top: auto;
}

.menu-item {
  margin-bottom: 10px;
}

.menu-group {
  margin-bottom: 15px;
  overflow: hidden;
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  color: #ffffff;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-header:hover {
  background-color: #1a3a3d;
}

.menu-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.arrow-icon {
  font-size: 14px;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.submenu {
  padding-left: 20px;
  margin-top: 5px;
  overflow: hidden;
}

.submenu-visible {
  max-height: 500px;
}

.submenu-link {
  display: block;
  padding: 8px 10px;
  color: #cccccc;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.3s;
}

.submenu-link:hover {
  background-color: #1a3a3d;
  color: #ffffff;
}

.submenu-link.router-link-exact-active {
  background-color: #1a3a3d;
  color: #ffffff;
}

.menu-link {
  display: flex;
  align-items: center;
  padding: 10px;
  color: #ffffff;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.3s;
}

.menu-link:hover {
  background-color: #1a3a3d;
  color: #ffffff;
}

.menu-link.router-link-active {
  background-color: #1a3a3d;
  color: #ffffff;
}

.menu-link i,
.menu-title i {
  margin-right: 12px;
  width: 16px;
  text-align: center;
}

.menu-link span,
.menu-title span {
  margin-left: 4px;
}

.submenu-link {
  padding-left: 28px;
}

.mr-2 {
  margin-right: 8px;
}

:deep(.el-dropdown-menu__item) {
  display: flex;
  align-items: flex-start;
  padding-top: 8px;
  padding-bottom: 8px;
}

:deep(.el-dropdown-menu__item i) {
  width: 16px;
  text-align: center;
}

.notification-icon-bg {
  font-size: 14px;
  color: #27AE60;
  background-color: rgba(39, 174, 96, 0.1);
  padding: 0;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  margin-right: 8px;
  margin-top: 2px;
}

.notification-icon-bg i {
  font-size: 14px;
  color: #27AE60;
  margin-left: 4px;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-left: 300px;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #000000;
  border-bottom: 1px solid #333333;
  position: sticky;
  top: 0;
  z-index: 1000;
}

.left-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.pharmacy-display {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #ffffff;
  font-size: 14px;
}

.pharmacy-display i {
  font-size: 16px;
}

.inactive-indicator {
  font-size: 10px;
  padding: 2px 6px;
  height: auto;
  line-height: 1.2;
}

.right-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.topbar-scanner :deep(.el-button) {
  background-color: #f5f7fa;
  border-color: #dcdfe6;
  color: #606266;
}

.topbar-scanner :deep(.el-button:hover) {
  background-color: #409eff;
  border-color: #409eff;
  color: #ffffff;
}

.content-wrapper {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.notification-badge {
  margin-right: 10px;
}

.notification-loading {
  padding: 20px;
  text-align: center;
  color: #909399;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

.loading-spinner {
  font-size: 24px;
  color: #409EFF;
  margin-bottom: 12px;
}

.loading-spinner i {
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 14px;
  color: #606266;
  margin-bottom: 20px;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.notification-empty {
  padding: 32px 16px;
  text-align: center;
  color: #909399;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.empty-icon {
  font-size: 28px;
  color: #c0c4cc;
  opacity: 0.8;
}

.empty-message {
  margin: 0;
  font-size: 14px;
  color: #909399;
  font-weight: 500;
}

.notification-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notification-title {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  line-height: 1.4;
  margin: 4px 0 0 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.notification-time {
  font-size: 12px;
  color: #909399;
  margin: 4px 0 0 0;
}

:deep(.el-dropdown-menu__item.unread) {
  background-color: #f8f9ff;
  border-left: 3px solid #409eff;
}

:deep(.el-dropdown-menu__item.unread:hover) {
  background-color: #e6f7ff;
}

:deep(.el-dropdown-menu__item.more-notifications-btn) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-weight: 600 !important;
  color: #3b82f6 !important;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%) !important;
  border-top: 1px solid #f3f4f6 !important;
  margin: 8px 12px 8px 12px !important;
  border-radius: 10px !important;
  box-shadow: none !important;
  min-height: 40px !important;
  transition: all 0.2s ease !important;
}

:deep(.el-dropdown-menu__item.more-notifications-btn:hover) {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%) !important;
  color: #1d4ed8 !important;
  transform: translateY(-1px) !important;
  box-shadow: none !important;
}

/* Backup styles for notification dropdown using inset */
.notification-badge :deep(.el-dropdown-menu) {
  width: 700px !important;
  position: absolute !important;
  inset: 59px 72px auto auto !important;
  z-index: 9999 !important;
  height: 600px !important;
  max-height: 600px !important;
  overflow-y: auto !important;
  visibility: visible !important;
  opacity: 1 !important;
  transform: none !important;
}

/* Mobile positioning */
@media (max-width: 768px) {
  .notification-badge :deep(.el-dropdown-menu) {
    width: 400px !important;
    inset: 59px 20px auto auto !important;
    height: 500px !important;
    max-height: 500px !important;
  }
}

/* Modern notification item design - compact (notification dropdown only) */
.notification-badge + .el-popper :deep(.el-dropdown-menu__item),
.notification-dropdown + .el-popper :deep(.el-dropdown-menu__item) {
  padding: 0 16px !important;
  border-bottom: 1px solid #f5f5f5 !important;
  border-radius: 0 !important;
  transition: background-color 0.2s ease, box-shadow 0.2s ease !important;
  cursor: pointer !important;
  min-height: 60px !important;
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
}

.notification-badge + .el-popper :deep(.el-dropdown-menu__item:hover),
.notification-dropdown + .el-popper :deep(.el-dropdown-menu__item:hover) {
  background-color: #f8fafc !important;
  box-shadow: inset 3px 0 0 #3b82f6 !important;
}

.notification-badge + .el-popper :deep(.el-dropdown-menu__item.unread),
.notification-dropdown + .el-popper :deep(.el-dropdown-menu__item.unread) {
  background-color: #eff6ff !important;
  border-left: 4px solid #3b82f6 !important;
  padding-left: 0 !important;
}

.notification-badge + .el-popper :deep(.el-dropdown-menu__item.unread:hover),
.notification-dropdown + .el-popper :deep(.el-dropdown-menu__item.unread:hover) {
  background-color: #dbeafe !important;
}

.notification-badge + .el-popper :deep(.el-dropdown-menu__item:last-child),
.notification-dropdown + .el-popper :deep(.el-dropdown-menu__item:last-child) {
  border-bottom: none;
}

/* Account dropdown styling to match notification dropdown */
.account-dropdown + .el-popper .el-dropdown-menu {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 16px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  padding: 8px 0 !important;
}

/* Override .el-dropdown-menu__item padding specifically for account dropdown - MAXIMUM SPECIFICITY */
html body .right-section .account-dropdown.el-dropdown + .el-popper .el-dropdown-menu .el-dropdown-menu__item[data-el-collection-item] {
  padding: 16px 20px !important;
  padding-top: 16px !important;
  padding-bottom: 16px !important;
  padding-left: 20px !important;
  padding-right: 20px !important;
}

.account-dropdown + .el-popper .el-dropdown-menu__item {
  padding: 16px 20px !important;
}

/* More specific targeting with deep */
.account-dropdown + .el-popper :deep(.el-dropdown-menu__item) {
  padding: 16px 20px !important;
}

/* Element-specific targeting */
.account-dropdown + .el-popper li.el-dropdown-menu__item {
  padding: 16px 20px !important;
}

/* Maximum specificity override */
.account-dropdown.el-dropdown + .el-popper .el-dropdown-menu .el-dropdown-menu__item {
  padding: 16px 20px !important;
}

/* Nuclear option - inline style override */
.account-dropdown + .el-popper .el-dropdown-menu__item[style] {
  padding: 16px 20px !important;
}

/* Even more specific - target by role */
.account-dropdown + .el-popper li[role="menuitem"].el-dropdown-menu__item {
  padding: 16px 20px !important;
}

.account-dropdown + .el-popper :deep(.el-dropdown-menu__item:hover) {
  background-color: #f8fafc !important;
  box-shadow: inset 3px 0 0 #3b82f6 !important;
}

.account-dropdown + .el-popper :deep(.el-dropdown-menu__item:last-child) {
  border-bottom: none !important;
}

/* Account dropdown header */
.account-dropdown + .el-popper .el-dropdown-menu::before {
  content: 'Account' !important;
  display: block !important;
  padding: 8px 16px 4px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  color: #111827 !important;
  border-bottom: 1px solid #f3f4f6 !important;
  margin: -8px 0 0 !important;
  background: #fafafa !important;
  border-radius: 16px 16px 0 0 !important;
}



/* Modern notification content layout */
.notification-preview {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  flex: 1;
  min-width: 0; /* Prevent flex overflow */
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;
  margin: 4px 0 0 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

.notification-description {
  font-size: 11px;
  color: #6b7280;
  font-weight: 400;
  line-height: 1.3;
  margin: 2px 0 0 0;
  font-style: italic;
  background-color: #f8f9fa;
  padding: 3px 6px;
  border-radius: 3px;
  border-left: 2px solid #e5e7eb;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
}

.notification-time {
  font-size: 12px;
  color: #6b7280;
  font-weight: 400;
  margin: 4px 0 0 0;
  letter-spacing: 0.025em;
}

/* Notification metadata */
.notification-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.notification-status {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.notification-status.new {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.notification-status.read {
  background-color: #f3f4f6;
  color: #6b7280;
}

/* Dashboard-style notification icon design */
.notification-icon-bg {
  font-size: 14px;
  color: #27AE60;
  background-color: rgba(39, 174, 96, 0.1);
  padding: 0;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  margin-top: 2px;
}

.notification-icon-bg i {
  font-size: 14px;
  color: #27AE60;
  margin-left: 4px;
}

.hamburger-button {
  display: none;
}

.close-button {
  display: none;
}

@media (max-width: 768px) {
  .hamburger-button {
    display: block;
  }

  .close-button {
    display: block;
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 100;
  }
  
  .logo-container {
    margin-bottom: 0;
  }
  
  .sidebar {
    display: none;
  }

  .sidebar-mobile {
    display: flex;
    width: 280px;
    background: linear-gradient(180deg, #000000 0%, rgba(64,109,112,143) 100%) !important;
    padding: 20px;
    padding-top: 80px;
    flex-direction: column;
    border-right: 1px solid #333333;
  }

  .sidebar-open {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0;
  }
}

:deep(.el-button) {
  --el-button-hover-bg-color: #f5f7fa;
  --el-button-hover-text-color: #606266;
  --el-button-hover-border-color: #dcdfe6;
}

:deep(.el-dropdown-menu__item:hover) {
  background-color: #f5f7fa;
  color: #606266;
}

:deep(.el-badge__content) {
  background-color: #409EFF;
}

/* Animation for submenu */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-5px);
  opacity: 0;
}

/* Premium notification dropdown design */
.notification-badge + .el-popper .el-dropdown-menu,
.notification-dropdown + .el-popper .el-dropdown-menu,
.right-section .notification-badge + .el-popper .el-dropdown-menu,
.right-section .notification-dropdown + .el-popper .el-dropdown-menu {
  width: 336px !important;
  min-width: 336px !important;
  max-width: 336px !important;
  height: auto !important;
  min-height: 120px !important;
  max-height: 480px !important;
  position: absolute !important;
  top: 100% !important;
  right: 0 !important;
  left: auto !important;
  margin-top: 12px !important;
  z-index: 9999 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 16px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  transform: translateX(-80%) !important;
  transition: none !important;
  box-sizing: border-box !important;
  visibility: visible !important;
  opacity: 1 !important;
  padding: 8px 0 !important;
}

/* Dropdown header - compact */
.notification-badge + .el-popper .el-dropdown-menu::before,
.notification-dropdown + .el-popper .el-dropdown-menu::before {
  content: 'Notifications' !important;
  display: block !important;
  padding: 8px 16px 4px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  color: #111827 !important;
  border-bottom: 1px solid #f3f4f6 !important;
  margin: -8px 0 0 !important;
  background: #fafafa !important;
  border-radius: 16px 16px 0 0 !important;
}

/* Force arrow positioning with exclusions for account dropdown */
div[data-popper-placement] .el-popper__arrow:not(.user-arrow),
.notification-badge + .el-popper .el-popper__arrow,
.notification-dropdown + .el-popper .el-popper__arrow,
.right-section .notification-badge + .el-popper .el-popper__arrow,
.right-section .notification-dropdown + .el-popper .el-popper__arrow {
  position: absolute !important;
  left: auto !important;
  right: 30px !important; /* Aligned with notification bell */
  top: -6px !important;
  transform: none !important;
  transition: none !important;
  width: 12px !important;
  height: 12px !important;
  z-index: 10000 !important;
}

.el-popper[data-popper-placement] .el-popper__arrow::before,
.notification-badge + .el-popper .el-popper__arrow::before,
.notification-dropdown + .el-popper .el-popper__arrow::before {
  position: absolute !important;
  left: 0 !important;
  right: auto !important;
  transform: none !important;
  transition: none !important;
}

/* Force notification popper container dimensions with highest specificity */
.notification-badge + .el-popper,
.notification-dropdown + .el-popper,
.right-section .notification-badge + .el-popper,
.right-section .notification-dropdown + .el-popper {
  position: absolute !important;
  transform: none !important;
  transition: none !important;
  width: 336px !important;
  height: auto !important;
  min-width: 336px !important;
  max-width: 336px !important;
  min-height: 120px !important;
  max-height: 480px !important;
}

/* Target notification dropdown with exclusions for account dropdown */
div[data-popper-placement="bottom-end"].el-popper .el-dropdown-menu:not(.user-dropdown),
div[data-popper-placement="bottom-start"].el-popper .el-dropdown-menu:not(.user-dropdown),
div[data-popper-placement="bottom"].el-popper .el-dropdown-menu:not(.user-dropdown) {
  width: 336px !important;
  min-width: 336px !important;
  max-width: 336px !important;
}

/* Additional fallback targeting notification specifically */
.notification-badge + .el-popper .el-dropdown-menu,
.notification-dropdown + .el-popper .el-dropdown-menu {
  width: 336px !important;
  min-width: 336px !important;
  max-width: 336px !important;
}

/* Override inline styles for notification dropdown only */
.notification-badge ~ .el-popper .el-dropdown-menu[style],
.notification-dropdown ~ .el-popper .el-dropdown-menu[style] {
  width: 336px !important;
}

/* Reduce account dropdown width specifically - multiple approaches */
.right-section > .el-dropdown:not(.notification-dropdown) + .el-popper .el-dropdown-menu,
.right-section > .el-dropdown:last-child + .el-popper .el-dropdown-menu,
.right-section .el-dropdown:not(.notification-dropdown) + .el-popper .el-dropdown-menu,
.right-section .el-dropdown:last-child + .el-popper .el-dropdown-menu {
  width: 220px !important;
  min-width: 220px !important;
  max-width: 220px !important;
}

/* Target account dropdown popper directly */
.right-section > .el-dropdown:not(.notification-dropdown) + .el-popper,
.right-section > .el-dropdown:last-child + .el-popper {
  width: 220px !important;
  min-width: 220px !important;
  max-width: 220px !important;
}

/* Override any existing account dropdown styles with specific class targeting */
.account-dropdown + .el-popper .el-dropdown-menu,
.account-dropdown + .el-popper .account-dropdown-menu,
div[data-popper-placement].el-popper .account-dropdown-menu {
  width: 220px !important;
  min-width: 220px !important;
  max-width: 220px !important;
}

/* Force account dropdown popper container size */
.account-dropdown + .el-popper {
  width: 220px !important;
  min-width: 220px !important;
  max-width: 220px !important;
}

/* Higher specificity for notification dropdown - using inset positioning */
.right-section .notification-dropdown .el-dropdown-menu,
.right-section .notification-dropdown :deep(.el-dropdown-menu),
.notification-dropdown.el-dropdown .el-dropdown-menu,
.notification-dropdown.el-dropdown :deep(.el-dropdown-menu) {
  width: 700px !important;
  min-width: 700px !important;
  max-width: 700px !important;
  position: absolute !important;
  inset: 59px 72px auto auto !important;
  z-index: 9999 !important;
  height: 600px !important;
  max-height: 600px !important;
  overflow-y: auto !important;
  visibility: visible !important;
  opacity: 1 !important;
  transform: none !important;
  box-sizing: border-box !important;
  background-color: #ffffff !important;
  border: 1px solid #e4e7ed !important;
}

/* Fix arrow positioning for all selectors */
.right-section .notification-dropdown .el-dropdown-menu::before,
.right-section .notification-dropdown .el-dropdown-menu::after,
.right-section .notification-dropdown :deep(.el-dropdown-menu::before),
.right-section .notification-dropdown :deep(.el-dropdown-menu::after),
.notification-dropdown.el-dropdown .el-dropdown-menu::before,
.notification-dropdown.el-dropdown .el-dropdown-menu::after {
  left: auto !important;
  right: 50px !important;
}

@media (max-width: 768px) {
  .right-section .notification-dropdown .el-dropdown-menu,
  .right-section .notification-dropdown :deep(.el-dropdown-menu),
  .notification-dropdown.el-dropdown .el-dropdown-menu,
  .notification-dropdown.el-dropdown :deep(.el-dropdown-menu) {
    width: 400px !important;
    min-width: 400px !important;
    max-width: 400px !important;
    inset: 59px 20px auto auto !important;
    height: 500px !important;
    max-height: 500px !important;
  }
  
  /* Mobile arrow positioning */
  .right-section .notification-dropdown .el-dropdown-menu::before,
  .right-section .notification-dropdown .el-dropdown-menu::after,
  .notification-dropdown.el-dropdown .el-dropdown-menu::before,
  .notification-dropdown.el-dropdown .el-dropdown-menu::after {
    right: 30px !important;
  }
}

/* Custom notification message styling */
:deep(.notification-success-message .el-notification__icon) {
  color: #3b82f6 !important; /* Blue color */
  font-size: 20px !important;
}

:deep(.notification-success-message .el-notification__icon .fas.fa-bell) {
  color: #3b82f6 !important; /* Blue color for bell icon */
}

/* Header barcode scanner modal should now work with default z-index from component */

/* Email Verification Banner */
.email-verification-banner {
  background-color: #fff3cd;
  border-bottom: 1px solid #ffc107;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #856404;
}

.email-verification-banner i {
  color: #ff9800;
  font-size: 18px;
}
</style>
